import { appStore } from '@/store';
import { redirectToGenericError } from '@/lib/utils';
import type { errorType } from '../types';

/**
 * Pre Account Creation Errors errors are errors that occur during or before
 * the onboarding process that aren't a result of a 500 by GQL, in which case
 * we redirect to xoon or hermes
 */

// These errors are displayed on a particular field
const FIELD_LEVEL_ERRORS = {
  DOB_INVALID: {
    contentKey: 'fieldErrors.dobLegalAge',
    field: 'dob',
  },
  FIRST_NAME_MUST_BE_IN_ENGLISH: {
    content: 'fieldErrors.firstNameMustBeInEnglish',
    field: 'firstName',
  },
  INVALID_CITY: {
    contentKey: 'fieldErrors.cityInvalid',
    field: 'city',
  },
  INVALID_EMAIL_ID: {
    contentKey: 'fieldErrors.emailInvalid',
    field: 'email',
  },
  INVALID_FIRST_NAME: {
    contentKey: 'fieldErrors.firstNameInvalid',
    field: 'firstName',
  },
  INVALID_FIRST_NAME_KANA: {
    contentKey: 'fieldErrors.useKanaNames',
    field: 'countrySpecificFirstName',
  },
  INVALID_IDENTITY_DOCUMENT_VALUE: {},
  INVALID_LAST_NAME: {
    contentKey: 'fieldErrors.lastNameInvalid',
    field: 'lastName',
  },
  INVALID_LAST_NAME_KANA: {
    contentKey: 'fieldErrors.useKanaNames',
    field: 'countrySpecificLastName',
  },
  INVALID_LINE1: {
    contentKey: 'fieldErrors.line1Invalid',
    field: 'line1',
  },
  INVALID_LINE1_POBOX: {
    contentKey: 'fieldErrors.primaryResidentialAddress.invalidPOBox',
    errorCode: 'RESIDENTIAL_ADDRESS_INVALID',
    field: 'line1',
  },
  INVALID_LINE1_RRBOX: {
    contentKey: 'fieldErrors.primaryResidentialAddress.invalidRRBox',
    errorCode: 'RESIDENTIAL_ADDRESS_INVALID',
    field: 'line1',
  },
  INVALID_MIDDLE_NAME: {
    contentKey: 'fieldErrors.middleNameInvalid',
    field: 'middleName',
  },
  INVALID_NATIONAL_ID: {
    contentKey: 'fieldErrors.identityDocumentNumberInvalid',
    field: 'identityDocumentNumber',
  },
  INVALID_NATIONAL_ID_TYPE: {
    contentKey: 'fieldErrors.identityDocumentTypeInvalid',
    field: 'identityDocumentType',
  },
  INVALID_PHONE_NUMBER: {
    contentKey: 'fieldErrors.phoneNumberInvalid',
    field: 'phone',
  },
  INVALID_PHONE_TYPE: {
    contentKey: '',
    field: 'phoneType',
  },
  INVALID_SECONDARY_ID: {
    contentKey: 'fieldErrors.secondaryIdentityDocumentNumberInvalid',
    field: 'secondaryIdentityDocumentNumber',
  },
  INVALID_SECONDARY_ID_TYPE: {
    contentKey: 'fieldErrors.secondaryIdentityDocumentTypeInvalid',
    field: 'secondaryIdentityDocumentType',
  },
  INVALID_STATE: {
    contentKey: 'fieldErrors.stateInvalid',
    field: 'state',
  },
  INVALID_ZIPCODE: {
    contentKey: 'fieldErrors.postalCodeInvalid',
    field: 'postalCode',
  },
  LAST_NAME_MUST_BE_IN_ENGLISH: {
    content: 'fieldErrors.lastNameMustBeInEnglish',
    field: 'lastName',
  },
  PASSWORD_TOO_LONG: {
    contentKey: 'fieldErrors.passwordLength',
    field: 'password',
  },
} as const;

// These errors are displayed above the form
const PAGE_LEVEL_ERRORS: Record<string, () => string> = {
  ACCOUNT_ALREADY_EXISTS: (): string => 'pageLevelError.accountExistsSignup',
  ADDRESS_VALIDATION_ERROR: (): string => 'pageLevelError.invalidAddress',
  BAD_INSTRUMENT: (): string => 'pageLevelError.cannotAddBank',
  BANK_GENERIC_ERROR: (): string => 'pageLevelError.bankGenericError',
  BANK_LINKED_TO_FULL_ACCOUNT: (): string => 'pageLevelError.bankLinked',
  BIN_BLOCKED_OR_CARD_NOT_ALLOWED: (): string =>
    'pageLevelError.cardNotAllowed',
  CARD_GENERIC_ERROR: (): string => 'pageLevelError.cardGenericError',
  CC_INVALID_CSC: (): string => 'pageLevelError.invalidSecurityCode',
  CC_LINKED: (): string => 'pageLevelError.ccLinked',
  CC_LINKED_TO_FULL_ACCOUNT: (): string => 'pageLevelError.ccLinked',
  CHECK_DIGITS_REJECTED: (): string => 'pageLevelError.invalidSecurityCode',
  COUNTRY_NOT_SUPPORTED: (): string => 'pageLevelError.cardNotAllowed',
  CREATE_CARD_ACCOUNT_CANDIDATE_VALIDATION_ERROR(): string {
    return 'pageLevelError.cardGenericError';
  },
  EXPIRED_CARD: (): string => 'pageLevelError.expiredCard',
  EXPIRED_CREDIT_CARD: (): string => 'pageLevelError.expiredCard',
  GUEST_CARD_COUNTRY_MISMATCH: (): string => 'pageLevelError.cardGenericError',
  INSTRUMENT_DISABLED: (): string => 'pageLevelError.cardDecline',
  INSTRUMENT_DUPLICATE_LIMIT_REACHED: (): string =>
    'pageLevelError.cardDecline',
  // INSTRUMENT_INTERNATIONAL: 'content.key',
  INSTRUMENT_REFUSED: (): string => 'pageLevelError.cardDecline',
  INSTRUMENT_REFUSED_DUE_TO_MAX_LIMIT: (): string =>
    'pageLevelError.cardDecline',
  INTERNAL_ERROR: (): string => 'pageLevelError.cardInternalError',
  INVALID_CARD_NUMBER: (): string => 'pageLevelError.invalidCardNumOrType',
  INVALID_CARD_OR_TYPE: (): string => 'pageLevelError.invalidCardNumOrType',
  INVALID_CARD_TYPE: (): string => 'pageLevelError.invalidCardNumOrType',
  // INVALID_CITY_STATE_ZIPCODE: 'content.key',
  INVALID_EXPIRATION_DATE: (): string => 'pageLevelError.invalidExpiry',
  INVALID_EXPIRY: (): string => 'pageLevelError.invalidExpiry',
  INVALID_INSTRUMENT_ID: (): string => 'pageLevelError.invalidInstrumentId',
  INVALID_SECURITY_CODE: (): string => 'pageLevelError.invalidSecurityCode',
  INVALID_SHIPPING_METHOD_FOR_ADDRESS: (): string =>
    'pageLevelError.invalidAddress',
  ISSUER_REJECTED: (): string => 'pageLevelError.cardGenericError',
  ISSUER_RESTRICTED: (): string => 'pageLevelError.cardGenericError',
  MAX_AMOUNT_EXCEEDED: (): string => 'pageLevelError.maxAmountExceeded',
  MISSING_SHIPPING_ADDRESS: (): string => 'pageLevelError.invalidAddress',
  NEED_CREDIT_CARD: (): string => 'pageLevelError.cardDecline',
  NEED_CREDIT_CARD_OR_BANK_ACCOUNT: (): string => {
    const { ui: uiState } = appStore.getState();
    const { fiType } = uiState;

    if (fiType === 'bank') {
      return 'pageLevelError.cannotUseBank';
    }
    return 'pageLevelError.cardDecline';
  },
  REJECT_REFUSED: (): string => 'pageLevelError.cardGenericError',
  REJECT_UNKNOWN: (): string => 'pageLevelError.cardGenericError',
  RISK_DISALLOWED: (): string => 'pageLevelError.cardNotAllowed',
  SETTLEMENT_DENIED: (): string => 'pageLevelError.cardDecline',
  SHIPPING_REGION_UNSUPPORTED: (): string =>
    'pageLevelError.shippingRegionUnsupported',
  SIGNUP_HOME_ADDRESS_INVALID: (): string => 'pageLevelError.invalidAddress',
  // TOO_MANY_REQUESTS: 'content.key',
} as const;

// Other preAccountCreationErrors, including redirect to generic error
const PRE_ACCOUNT_CREATION_ERRORS: Record<string, (e: errorType) => void> = {
  /**
   * OAS_ERROR_UNRECOVERABLE is a bucket for any OAS_ERROR we've researched,
   * know the underlying cause, and have determined the only course of action is
   * to display the generic error page with the default content. The error
   * response from OAS must be mapped to an OAS_ERROR with an errorData of
   * [{ code: OAS_ERROR_UNRECOVERABLE }] for this to fire.
   */
  OAS_ERROR_UNRECOVERABLE: redirectToGenericError,
  UNMAPPED_IDENTITY_DOCUMENT_ERROR: redirectToGenericError,
  /**
   * UNMAPPED_OAS_ERROR is a bucket for unexpected OAS errors. We need to
   * research the underlying reason for these errors, provide additional
   * context for the error in xobuyer, then add it to one of these treatment
   * maps.
   */
  UNMAPPED_OAS_ERROR: redirectToGenericError,
};

export { FIELD_LEVEL_ERRORS, PAGE_LEVEL_ERRORS, PRE_ACCOUNT_CREATION_ERRORS };
